import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import { useFormik } from "formik";
import * as yup from "yup";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Typography } from "@mui/material";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import { contactFormOperations } from "../../ContactUs/duck/index";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import ChatIcon from "@mui/icons-material/Chat";
import Fade from "@mui/material/Fade";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

const PREFIX = "FeedbackForm";

const classes = {
  fixedLocation: `${PREFIX}-fixedLocation`,
  fabFixedLocation: `${PREFIX}-fabFixedLocation`,
  GridContainer: `${PREFIX}-GridContainer`,
  cancelButton: `${PREFIX}-cancelButton`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.fixedLocation}`]: {
    position: "fixed",
    top: 7,
    right: 72,
    zIndex: 1,
    width: "500px",
    maxWidth: "3000vw", 
    [theme.breakpoints.up(1000 + theme.spacing(3) * 2)]: {
      width: "35vw",
    },
    [theme.breakpoints.up(1500 + theme.spacing(3) * 2)]: {
      width: "25vw",
    },
  },

  [`& .${classes.fabFixedLocation}`]: {
    position: "fixed",
    top: 8,
    right: 76,
    zIndex: 1,
    color: "#fff",
  },

  [`& .${classes.GridContainer}`]: {
    padding: theme.spacing(2),
  },

  [`& .${classes.cancelButton}`]: {
    position: "absolute",
    right: "0px",
    top: "0px",
  },
}));

const validationSchema = yup.object({
  message: yup.string().max(512, "Too Long!").required("Required"),
});

const FeedbackForm = (props) => {
  const [showSnackBar, setShowSnackBar] = useState();
  const [fabMounted, setFabMounted] = useState(true);
  const [feedbackMounted, setFeedbackMounted] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const formik = useFormik({
    initialValues: {
      message: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, actions) => {
      actions.setSubmitting(true);
      await props.sendContactMessage(values);
      actions.resetForm();
      setShowSnackBar(true);
    },
  });

  return (
    <Root>
      <Snackbar
        open={showSnackBar && props.success}
        onClose={() => setShowSnackBar(false)}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert variant="filled" severity={props.success ? "success" : "error"}>
          {props.success
            ? "Thank you for your feedback"
            : "Uh Oh! Something went wrong"}
        </Alert>
      </Snackbar>
      <Fade
        onEnter={() => setFabMounted(true)}
        onExited={() => setFabMounted(false)}
        mountOnEnter
        unmountOnExit
        in={!feedbackMounted && !showForm}
      >
        <Tooltip title="Ideas form">
          <IconButton
            className={classes.fabFixedLocation}
            size="medium"
            onClick={() => setShowForm(true)}
          >
            <ChatIcon style={{ color: "white", fontSize: 26 }} />
          </IconButton>
        </Tooltip>
      </Fade>
      <Fade
        onEnter={() => setFeedbackMounted(true)}
        onExited={() => setFeedbackMounted(false)}
        mountOnEnter
        unmountOnExit
        in={!fabMounted && showForm}
      >
        <Paper elevation={5} className={classes.fixedLocation}>
          <IconButton
            className={classes.cancelButton}
            onClick={() => setShowForm(false)}
            size="large"
          >
            <CloseIcon />
          </IconButton>
          <form onSubmit={formik.handleSubmit}>
            <Grid className={classes.GridContainer} container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" component={"span"}>
                  Give us ideas for new features!
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component={"span"}
                >
                  Please let us know if you discover any bugs or if you have any
                  ideas for ways to improve the dashboard such as structure
                  changes, new components, or new pages.
                </Typography>
              </Grid>
              <Grid id="userinput" item xs={12}>
                <TextField
                  fullWidth
                  id="message"
                  name="message"
                  label="I think the dashboard could be improved by..."
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.message && Boolean(formik.errors.message)
                  }
                  helperText={formik.touched.message && formik.errors.message}
                  multiline
                />
              </Grid>
              <Grid container justifyContent="center" item xs={12}>
                <Button
                  startIcon={
                    formik.isSubmitting ? (
                      <CircularProgress size="1rem" />
                    ) : null
                  }
                  disabled={formik.isSubmitting}
                  color="primary"
                  variant="text"
                  type="submit"
                >
                  Submit Feedback
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Fade>
    </Root>
  );
};

FeedbackForm.propTypes = {
  sendContactMessage: PropTypes.func,
  success: PropTypes.bool,
};

const mapStateToProps = (state) => {
  return {
    success: state.contactUsReducer.success,
  };
};
const mapDispatchToProps = (dispatch) => ({
  sendContactMessage: (userMessage) =>
    dispatch(contactFormOperations.sendContactMessage(userMessage)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(FeedbackForm)
);
